import type { AxiosError } from 'axios';
import { getInGameBalance } from '@/api/in-game-balance/fetch-in-game-balance';
import { useStore } from '@/store/useStore';
import { startTimer } from '@/utilities/helpers/startTimer';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { isInGameBalanceEnabled } from './is-in-game-balance-enabled';

/** FGP overlay merged onto the wallet balance when the in-game balance experiment is active. */
export type InGameBalanceEnrichment = {
  total: number;
  inGamePlayableBalance?: number;
  inGameEligibleBonus?: number;
  inGameDeposits?: number;
  inGameSharedWinnings?: number;
  inGameBalanceActive: true;
};

/**
 * Flag-gated FGP in-game balance enrichment. Owns the try/catch + timing +
 * Datadog status logging (the fetcher is pure transport). Returns `null` when
 * the flag is off, the response has no balance, or the call fails — so a missing
 * or failed FGP call always leaves the plain wallet balance untouched.
 */
export const fetchInGameBalanceEnrichment =
  async (): Promise<InGameBalanceEnrichment | null> => {
    if (!isInGameBalanceEnabled()) return null;

    const { gameId, providerRef, providerUid } =
      useStore.getState().gameContext;
    const { userId } = useStore.getState().userSessionContext;

    if (!providerUid || !providerRef || !userId) return null;

    const elapsedMs = startTimer();

    Logger.logRumAction({
      name: '[GW][IN_GAME_BALANCE] AW Get Bonus Balance Initiated',
      useCase: DdEventCategory.Wallet,
      eventType: DdEventType.Info,
      logOnly: true,
      event: { action: 'AW Get Bonus Balance Initiated', log_code: 'GW-120' },
    });

    try {
      const response = await getInGameBalance({
        providerName: providerUid,
        rgsCode: providerRef,
        userId,
      });

      if (!response) return null;

      const { status, data } = response;

      if (status < 200 || status >= 300 || !data?.balance) {
        throw new Error(`In-game balance call failed with status ${status}`);
      }

      Logger.logRumAction({
        name: '[GW][IN_GAME_BALANCE] AW Get Bonus Balance Succeeded',
        useCase: DdEventCategory.Wallet,
        eventType: DdEventType.Success,
        context: {
          response_code: status,
          duration: elapsedMs(),
          game_id: gameId,
        },
        event: {
          action: 'AW Get Bonus Balance Succeeded',
          log_code: 'GW-220',
        }
      });

      const {
        total,
        playableBalance,
        eligibleBonus,
        sharedWinnings,
        deposits,
      } = data.balance;

      return {
        total,
        inGamePlayableBalance: playableBalance,
        inGameEligibleBonus: eligibleBonus,
        inGameDeposits: deposits,
        inGameSharedWinnings: sharedWinnings,
        inGameBalanceActive: true,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ errorCode?: string }>;
      const errorName =
        error instanceof Error && error.message.includes('[GW]')
          ? error.message
          : '[GW][IN_GAME_BALANCE] Status failed';

      Logger.logRumError({
        name: errorName,
        useCase: DdEventCategory.Wallet,
        eventType: DdEventType.Error,
        context: {
          response_code: axiosError?.response?.status ?? 0,
          duration: elapsedMs(),
          game_id: gameId,
          reason: axiosError?.message,
        },
        event: {
          action: 'AW Get Bonus Balance Failed',
          log_code: 'GW-520'
        }
      });

      return null;
    }
  };